import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronRightIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import DynamicField from './DynamicField';
import { applyConditionalLogic, validateFormData } from './FormLogicEngine';

const DynamicForm = ({ 
  clientId, 
  templateId = null, 
  onSubmit, 
  onSubmitSuccess, 
  onSubmitError,
  className = "",
  showTitle = true,
  showDescription = true 
}) => {
  const [formConfig, setFormConfig] = useState(null);
  const [formData, setFormData] = useState({});
  const [processedSchema, setProcessedSchema] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Fetch form configuration
  const fetchFormConfig = useCallback(async () => {
    if (!clientId) return;
    
    try {
      setIsLoading(true);
      setError(null);

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      // Try to get client-specific config first
      let response;
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add auth header if available (for internal use)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      try {
        const configUrl = templateId 
          ? `${backendUrl}/api/clients/${clientId}/form-config?template_id=${templateId}`
          : `${backendUrl}/api/clients/${clientId}/form-config`;
        
        response = await fetch(configUrl, { headers });
        
        if (response.status === 404) {
          // No client-specific config, try to get default template
          if (templateId) {
            const templateResponse = await fetch(`${backendUrl}/api/form-templates`, { headers });
            if (templateResponse.ok) {
              const templates = await templateResponse.json();
              const template = templates.find(t => t.id === templateId || t.is_default);
              if (template) {
                setFormConfig({
                  template_name: template.name,
                  default_schema: template.form_schema,
                  default_rules: template.conditional_rules || [],
                  default_settings: template.settings || {}
                });
                return;
              }
            }
          }
          throw new Error('Form configuration not found');
        }
        
        if (!response.ok) {
          throw new Error(`Failed to fetch form config: ${response.status}`);
        }
        
        const config = await response.json();
        setFormConfig(config);
        
      } catch (fetchError) {
        console.error('Error fetching form config:', fetchError);
        setError(fetchError.message);
      }
    } catch (err) {
      console.error('Form config fetch error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, templateId]);

  // Apply conditional logic when form data changes
  useEffect(() => {
    if (formConfig && formData) {
      const schema = formConfig.custom_schema || formConfig.default_schema;
      const rules = formConfig.custom_rules || formConfig.default_rules || [];
      
      if (schema) {
        const newProcessedSchema = applyConditionalLogic(schema, formData, rules);
        setProcessedSchema(newProcessedSchema);
      }
    }
  }, [formData, formConfig]);

  // Initialize form data with default values
  useEffect(() => {
    if (processedSchema && processedSchema.fields) {
      const initialData = {};
      processedSchema.fields.forEach(field => {
        if (field.default_value !== undefined && formData[field.id] === undefined) {
          initialData[field.id] = field.default_value;
        }
      });
      
      if (Object.keys(initialData).length > 0) {
        setFormData(prev => ({ ...prev, ...initialData }));
      }
    }
  }, [processedSchema]);

  // Load form configuration on mount
  useEffect(() => {
    fetchFormConfig();
  }, [fetchFormConfig]);

  // Update field value
  const updateField = useCallback((fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Validate form
  const validateForm = useCallback(() => {
    if (!processedSchema) return false;
    
    const errors = validateFormData(formData, processedSchema);
    setValidationErrors(errors);
    
    return Object.keys(errors).length === 0;
  }, [formData, processedSchema]);

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setSubmitSuccess(false);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add auth header if available (optional for anonymous submissions)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const submissionData = {
        client_id: clientId,
        form_config_id: formConfig?.id || null,
        template_id: templateId,
        submitted_data: formData
      };
      
      const response = await fetch(
        `${backendUrl}/api/clients/${clientId}/form-submissions`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(submissionData)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Submission failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      setSubmitSuccess(true);
      
      // Call success callbacks
      if (onSubmitSuccess) {
        onSubmitSuccess(result);
      }
      if (onSubmit) {
        onSubmit(result);
      }
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({});
        setSubmitSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message);
      
      if (onSubmitError) {
        onSubmitError(err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Memoized form fields
  const formFields = useMemo(() => {
    if (!processedSchema?.fields) return [];
    
    return processedSchema.fields.map(field => (
      <DynamicField
        key={field.id}
        field={field}
        value={formData[field.id] || ''}
        onChange={(value) => updateField(field.id, value)}
        error={validationErrors[field.id]}
        disabled={isSubmitting}
      />
    ));
  }, [processedSchema, formData, validationErrors, isSubmitting, updateField]);

  if (isLoading) {
    return (
      <div className={`dynamic-form ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`dynamic-form ${className}`}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Form Load Error</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              <button
                onClick={fetchFormConfig}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-300 underline"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!processedSchema) {
    return (
      <div className={`dynamic-form ${className}`}>
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">No form available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`dynamic-form ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Form Header */}
        {(showTitle || showDescription) && (
          <div className="form-header">
            {showTitle && formConfig?.template_name && (
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {formConfig.template_name}
              </h2>
            )}
            {showDescription && formConfig?.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {formConfig.description}
              </p>
            )}
          </div>
        )}

        {/* Success Message */}
        {submitSuccess && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  Form Submitted Successfully!
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Thank you for your submission. We'll get back to you soon.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && !isSubmitting && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Submission Error
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-6">
          {formFields}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || submitSuccess}
            className={`
              inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white
              ${isSubmitting || submitSuccess
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600'
              }
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500
              transition-colors duration-200
            `}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </>
            ) : submitSuccess ? (
              <>
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Submitted!
              </>
            ) : (
              <>
                Submit
                <ChevronRightIcon className="h-5 w-5 ml-2" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DynamicForm;